import Mango from './mango.js'
import { useCheckoutStore } from '../stores/store.js'

/** BC order statuses where checkout is finished (paid / fulfillment pipeline). */
export const FULFILLED_ORDER_STATUS_IDS = new Set([2, 3, 8, 9, 10, 11, 12, 14])

const AWAITING_PAYMENT_STATUS_ID = 7
const INCOMPLETE_STATUS_ID = 0

function orderPaymentMethod(order) {
	return String(order?.paymentMethod || order?.payment_method || '').toLowerCase()
}

/** ACH / NET30 complete checkout in Awaiting Payment — cart should still be cleared. */
export function isDeferredPaymentCheckoutComplete(order) {
	if (Number(order?.statusId) !== AWAITING_PAYMENT_STATUS_ID) return false
	const method = orderPaymentMethod(order)
	return method.includes('ach') || method.includes('net 30') || method.includes('net30')
}

export function isFulfilledOrder(order) {
	return order?.statusId != null && FULFILLED_ORDER_STATUS_IDS.has(Number(order.statusId))
}

/** Unpaid card order that can accept another PAT attempt (Incomplete, or legacy failed-card status 7). */
export function isCardPaymentRetryOrder(order) {
	const statusId = Number(order?.statusId)
	if (statusId === INCOMPLETE_STATUS_ID) return true
	if (statusId === AWAITING_PAYMENT_STATUS_ID) return !isDeferredPaymentCheckoutComplete(order)
	return false
}

export function isCheckoutCompleteOrder(order) {
	return isFulfilledOrder(order) || isDeferredPaymentCheckoutComplete(order)
}

export async function getOrdersForCart(cart) {
	if (!cart?.bcId) return []
	const orders = await Mango.orders({ search: { cartId: cart.bcId } })
	return Array.isArray(orders) ? orders : []
}

/**
 * The cart loaded from the server is always an open one (GET /controllers/cart skips completed carts) and checkout
 * sets `completed` server-side, so the cart object itself is the only check needed here. Reading the carts
 * collection again was redundant and 401s for guests, who cannot read it.
 */
function isCartMarkedComplete(cart) {
	return !!cart?.completed
}

/**
 * True when the cart's BC checkout has a completed order (cart should be cleared).
 * Restores checkout.bcOrderId when an unpaid card retry order exists for this cart.
 */
export async function cartHasFulfilledOrder(cart) {
	if (!cart?.bcId) return false

	if (isCartMarkedComplete(cart)) return true

	const orders = await getOrdersForCart(cart)
	if (!orders.length) return false

	if (orders.some(isCheckoutCompleteOrder)) return true

	const pending = orders.find(isCardPaymentRetryOrder)
	if (pending?.bcId != null) {
		try {
			useCheckoutStore().setBcOrderId(pending.bcId)
		} catch {
			// store may not be ready during very early init
		}
	}

	return false
}
